import {
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    InputBase,
    makeStyles,
    Typography,
} from "@material-ui/core";
import CalendarMonthIcon from "@material-ui/icons/CalendarTodayTwoTone";
import CloseIcon from "@material-ui/icons/Close";
import { useState } from "react";
import { DateRangePicker } from "react-date-range";
import * as locales from "react-date-range/dist/locale";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

function SearchBar(props) {
    const {
        placeholder = "Search...",
        setSearchValue = () => { },
        searchValue,
        isMobile = false,
        onDateRangeChange = () => { },
        locale = "enUS",
        reset = false,
    } = props;
    const useStyles = makeStyles((theme) => ({
        searchContainer: {
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            height: "32px",
            gap: theme.spacing(1),
            alignItems: "center",
            backgroundColor: "#ffffff",
            border: `1px solid #313C4C`,
            borderRadius: "6px",
            padding: "0px 8px",
            boxShadow: "0px 2px 4px rgba(16, 63, 107, 0.08)",
            transition: "border-color 0.2s ease",
            "&:focus-within": {
                border: `1.5px solid #313C4C`,
                boxShadow: "0 0 0 2px rgba(16, 63, 107, 0.15)",
            },
        },
        input: {
            fontSize: "12px",
            color: "#313C4C",
            width: "100%",
            outline: "none",
            border: "none",
            "&:focus": {
                outline: "none",
            },
        },
        inputMobile: {
            fontSize: "12px",
            color: "#313C4C",
            width: "100%",
            minWidth: 0,
            outline: "none",
            border: "none",
            "&:focus": {
                outline: "none",
            },
        },
        calendarIcon: {
            color: "#313C4C",
            cursor: "pointer",
            fontSize: "18px",
            marginLeft: "6px",
            transition: "transform 0.2s ease",
            "&:hover": {
                transform: "scale(1.1)",
            },
        },
        dateDisplay: {
            marginLeft: "8px",
            color: "#313C4C",
            fontSize: "0.9rem",
            display: "flex",
            alignItems: "center",
            whiteSpace: "nowrap",
            [theme.breakpoints.down("sm")]: { fontSize: "11px", marginLeft: "4px" },
        },
        dialogPaper: {
            backgroundColor: "#f0f4f8",
            borderRadius: "12px",
            [theme.breakpoints.down("sm")]: { borderRadius: 0 },
        },
        resetButton: {
            marginLeft: "8px",
            color: "#f44336",
            padding: "0",
            [theme.breakpoints.down("sm")]: { marginLeft: "2px", "& svg": { fontSize: 16 } },
        },
        dialogTitle: {
            backgroundColor: "#313C4C",
            color: "white",
            textAlign: "center",
            borderTopLeftRadius: "12px",
            borderTopRightRadius: "12px",
            [theme.breakpoints.down("sm")]: {
                borderRadius: 0,
                padding: "10px 16px",
                "& .MuiTypography-root": { fontSize: 16, fontWeight: 500, lineHeight: 1.4 },
            },
        },
        dateRangePicker: {
            // Phones: stack the preset ranges above the calendar so nothing is cut off.
            [theme.breakpoints.down("sm")]: {
                width: "100%",
                "&.rdrDateRangePickerWrapper": { flexDirection: "column" },
                "& .rdrDefinedRangesWrapper": {
                    width: "100%",
                    display: isMobile ? "none" : "block",
                    borderRight: "none",
                    borderBottom: "solid 1px #eff2f7",
                },
                "& .rdrStaticRanges": { flexDirection: "row", flexWrap: "wrap" },
                "& .rdrStaticRange": { flex: "1 1 33%", borderBottom: "none" },
                "& .rdrInputRanges": { display: "flex", flexWrap: "wrap", gap: 8 },
                "& .rdrCalendarWrapper, & .rdrMonths, & .rdrMonth": { width: "100%" },
                "& .rdrMonthAndYearWrapper": { height: 44, paddingTop: 4 },
                "& .rdrDay": { height: "2.9em", lineHeight: "2.9em" },
                "& .rdrMonthAndYearPickers select[size]": {
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    zIndex: 10,
                    height: "auto",
                    maxHeight: 290,
                    overflowY: "auto",
                    padding: 0,
                    background: "#fff",
                    border: "1px solid #313C4C",
                    boxShadow: "0 4px 12px rgba(15, 23, 42, 0.18)",
                    backgroundImage: "none",
                    fontSize: 13,
                    color: "#313C4C",
                    textAlign: "center",
                    "& option": { padding: "6px 0" },
                    "& option:checked": { background: "#313C4C", color: "#fff" },
                },
                "& .rdrMonthAndYearPickers .rdrMonthPicker, & .rdrMonthAndYearPickers .rdrYearPicker": {
                    position: "relative",
                    minWidth: 96,
                    minHeight: 30,
                },
            },
            "& .rdrDefinedRangesWrapper": {
                backgroundColor: "#f0f4f8",
                display: isMobile ? "none" : "block",
            },
            " & .rdrDateDisplayItemActive": {
                borderColor: "#313C4C",
            },
            "& .rdrMonth": {
                backgroundColor: "white",
            },
            "& .rdrDay": {
                "&:hover": {
                    backgroundColor: "#e6f7ff",
                },
            },
            "& .rdrDayNumber": {
                color: "#313C4C",
                "& span": {
                    color: "#313C4C",
                },
            },
            "& .rdrSelected": {
                backgroundColor: "#313C4C !important",
                color: "white !important",
            },
            "& .rdrInRange": {
                backgroundColor: "#313C4C !important",
                color: "#313C4C !important",
            },
            "& .rdrStartEdge, & .rdrEndEdge": {
                backgroundColor: "#313C4C !important",
                color: "white !important",
            },
        },
    }));
    const classes = useStyles();

    const [openDatePicker, setOpenDatePicker] = useState(false);
    const [dateRange, setDateRange] = useState([
        {
            startDate: null,
            endDate: null,
            key: "selection",
        },
    ]);
    const [isDateRangeSelected, setIsDateRangeSelected] = useState(false);

    const handleDateRangeSelect = (ranges) => {
        const selectedRange = ranges.selection;

        setDateRange([selectedRange]);

        if (
            selectedRange.startDate &&
            selectedRange.endDate !== selectedRange.startDate
        ) {
            setIsDateRangeSelected(true);

            onDateRangeChange({
                startDate: selectedRange.startDate,
                endDate: selectedRange.endDate,
            });

            setOpenDatePicker(false);
        }
    };

    // Phones: drop the start year when both dates share it ("Oct 14 - Oct 23, 2026").
    const formatDate = (date, withYear = true) => {
        return date.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            ...(withYear && { year: "numeric" }),
        });
    };

    const isPhone = window.matchMedia("(max-width: 599px)").matches;
    const sameYear = dateRange[0].startDate?.getFullYear() === dateRange[0].endDate?.getFullYear();

    // Phones: replace the browser's full-height native month/year popup with a short scrollable list.
    const getPickerSelect = (el) =>
        window.matchMedia("(max-width: 599px)").matches ? el.closest?.(".rdrMonthAndYearPickers select") : null;

    const closePickerList = (e) => {
        const select = getPickerSelect(e.target);
        if (select) select.removeAttribute("size");
    };

    const handlePickerMouseDown = (e) => {
        const select = getPickerSelect(e.target);
        if (!select || select.size > 1) return;
        e.preventDefault();
        select.size = Math.min(10, select.options.length);
        select.focus();
        requestAnimationFrame(() => {
            const selected = select.selectedOptions[0];
            if (selected) select.scrollTop = selected.offsetTop - (select.clientHeight - selected.offsetHeight) / 2;
        });
    };

    const handleDatePicker = () => {
        const shouldSetNewDateRange = !dateRange || !dateRange[0]?.startDate;

        if (shouldSetNewDateRange) {
            const newDateRange = [
                {
                    startDate: new Date(),
                    endDate: new Date(),
                    key: "selection",
                },
            ];
            setDateRange(newDateRange);
        }

        setOpenDatePicker(true);
    };

    const handleReset = () => {
        const newDateRange = [
            {
                startDate: null,
                endDate: null,
                key: "selection",
            },
        ];

        setDateRange(newDateRange);
        setIsDateRangeSelected(false);
        onDateRangeChange({ startDate: null, endDate: null });
    };

    return (
        <div>
            <div className={classes.searchContainer}>
                <form
                    autoComplete="off"
                    style={{ flex: 1, display: 'flex', minWidth: 0 }}
                >
                    <InputBase
                        fullWidth
                        placeholder={placeholder}
                        value={searchValue}
                        onChange={(e) => setSearchValue(e)}
                        classes={{ input: isMobile ? classes.inputMobile : classes.input }}
                        inputProps={{
                            "aria-label": "search",
                            autoComplete: "new-password",
                            name: "search-field-no-autofill",
                        }}
                    />
                </form>

                <div style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>

                    <CalendarMonthIcon
                        className={classes.calendarIcon}
                        onClick={handleDatePicker}
                    />
                    {isDateRangeSelected &&
                        dateRange[0].startDate &&
                        dateRange[0].endDate && (
                            <Typography className={classes.dateDisplay}>
                                {formatDate(dateRange[0].startDate, !(isPhone && sameYear))} -{" "}
                                {formatDate(dateRange[0].endDate)}
                            </Typography>
                        )}
                    {isDateRangeSelected && (
                        <IconButton className={classes.resetButton} onClick={handleReset}>
                            <CloseIcon />
                        </IconButton>
                    )}
                </div>
            </div>

            <Dialog
                open={openDatePicker}
                onClose={() => setOpenDatePicker(false)}
                maxWidth="md"
                classes={{ paper: classes.dialogPaper }}
            >
                <DialogTitle className={classes.dialogTitle}>
                    Select Date Range
                </DialogTitle>
                <DialogContent
                    onMouseDown={handlePickerMouseDown}
                    onBlur={closePickerList}
                    onChange={closePickerList}
                >
                    <DateRangePicker
                        className={classes.dateRangePicker}
                        onChange={handleDateRangeSelect}
                        moveRangeOnFirstSelection={false}
                        ranges={dateRange}
                        locale={locales[locale]}
                        months={1}
                        direction="horizontal"
                        showSelectionPreview={true}
                        editableDateInputs={true}
                        dragSelectionEnabled={true}
                    />
                </DialogContent>
            </Dialog>
        </div >
    );
}

export default SearchBar;
